import { Search } from "@mui/icons-material";
import { InputAdornment } from "@mui/material";
import React, { FC } from "react";
import { Control } from "react-hook-form";
import {
  FormController,
  MuiBox,
  MuiCheckbox,
  MuiGrid,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
} from "../../../../../../../../components";
import { MAX_LENGTH_75 } from "../../../../../../../../helpers/constants";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { activitiesAndDeliverablesSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import {
  ExistingDeliverableFilterFormProps,
  ExistingDeliverableFormKeys,
  ExistingDeliverablesType,
} from "../../../../modelsAndSchemas/models";
import { ExistingDeliverableWrapper } from "./ExistingDeliverables.style";

export interface CreateClientExistingDeliverableProps {
  onHeaderCheckBoxChange: (checked: boolean) => void;
  isAllSelected: boolean;
  pageId: string;
  handleRowCheckboxChange: (checked: boolean, id: string) => void;
  existingFilterFormControl: Control<ExistingDeliverableFilterFormProps>;
  tableRef: React.RefObject<{ resetPagination: () => void }>;
  existingDeliverablesList: ExistingDeliverablesType[];
  noDataInApi: boolean;
}
const CreateClientExistingDeliverables: FC<
  CreateClientExistingDeliverableProps
> = ({
  onHeaderCheckBoxChange,
  isAllSelected,
  pageId,
  handleRowCheckboxChange,
  existingFilterFormControl,
  tableRef,
  existingDeliverablesList,
  noDataInApi,
}) => {
  const {
    commonLabels,
    fetchDropdownValues,
    fetchLabel,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();

  const columns = [
    {
      id: ExistingDeliverableFormKeys.isSelected,
      label: "",
      className: "checkbox-cell",
      isSortVisible: false,
      headerRender: () => (
        <MuiCheckbox
          checked={isAllSelected}
          checkboxLabel={`${commonARIALabel.selectAll} ${fetchLabel(
            activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
            activitiesAndDeliverablesSiteCoreKeys.deliverableName,
          )}`}
          hideLabel={true}
          onCheckedChange={(checked: boolean) =>
            onHeaderCheckBoxChange(checked)
          }
        />
      ),
      render: (row: any) => {
        return (
          <MuiCheckbox
            checked={row.isSelected ?? false}
            checkboxLabel={row.name}
            hideLabel={true}
            id={`${pageId}-${row.id}`}
            onCheckedChange={(checked: boolean) => {
              handleRowCheckboxChange(checked, row.id);
            }}
          />
        );
      },
    },
    {
      id: ExistingDeliverableFormKeys.name,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
        activitiesAndDeliverablesSiteCoreKeys.deliverableName,
      ),
      isSortVisible: true,
      className: "cell-name",
    },

    {
      id: ExistingDeliverableFormKeys.deliverableTypeName,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
        activitiesAndDeliverablesSiteCoreKeys.deliverableType,
      ),
      isSortVisible: true,
    },
    {
      id: ExistingDeliverableFormKeys.unitName,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableUnits,
      ),
      isSortVisible: true,
    },
    {
      id: ExistingDeliverableFormKeys.measurements,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.deliverablesListKey,
        activitiesAndDeliverablesSiteCoreKeys.deliverablesMeasurement,
      ),
      render: (row: any) => {
        return isNaN(row.measurements)
          ? fetchValueByKeyOfDropdown(
              activitiesAndDeliverablesSiteCoreKeys.defaultActivityKey,
              activitiesAndDeliverablesSiteCoreKeys.minutesList,
              row.measurements,
            )
          : row.measurements;
      },
      isSortVisible: true,
    },
  ];

  const renderFormControl = (
    control: any,
    name: keyof typeof ExistingDeliverableFormKeys,
    options: OptionsDataType[],
    placeholder: string,
    id: string,
  ) => (
    <FormController control={control} name={name} key={name}>
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={options}
        value=""
        id={id}
        placeholder={placeholder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>
  );

  const tableHeaderForm = [
    <></>,
    <FormController
      control={existingFilterFormControl}
      name={ExistingDeliverableFormKeys.name}
      key={ExistingDeliverableFormKeys.name}
    >
      <MuiTextField
        id={`${pageId}-${ExistingDeliverableFormKeys.name}`}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_75}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    renderFormControl(
      existingFilterFormControl,
      ExistingDeliverableFormKeys.deliverableType,
      fetchDropdownValues(
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
        activitiesAndDeliverablesSiteCoreKeys.deliverableTypeList,
      ),
      commonLabels.selectPlaceHolder,
      `${pageId}-${ExistingDeliverableFormKeys.deliverableType}`,
    ),
    renderFormControl(
      existingFilterFormControl,
      ExistingDeliverableFormKeys.units,
      fetchDropdownValues(
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableUnitList,
      ),
      commonLabels.selectPlaceHolder,
      `${pageId}-${ExistingDeliverableFormKeys.units}`,
    ),
    <></>,
  ];
  return (
    <ExistingDeliverableWrapper>
      <MuiGrid container className="existing-header">
        <MuiGrid size={{ xs: 6 }} className="existing-deliverable">
          <MuiTypography variant="h2SemiBold" component="h2">
            {fetchLabel(
              activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
              activitiesAndDeliverablesSiteCoreKeys.existingDeliverables,
            )}
          </MuiTypography>
        </MuiGrid>
      </MuiGrid>

      <MuiBox className="table-container">
        <MuiGrid container data-testid="existing-deliverables">
          <MuiGrid size={{ xs: 12 }} className="header-right">
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={tableHeaderForm}
              columns={columns}
              tableResults={[...existingDeliverablesList]}
              noDataInApi={noDataInApi}
              noResultMessage={commonLabels.noResult}
              emptyMessage={commonLabels.noResult}
              priorityResults={[]}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
    </ExistingDeliverableWrapper>
  );
};

export default CreateClientExistingDeliverables;
